"use client";

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { KnownLanguageCode } from "./api/client";

interface TTSSettings {
  speed: number;
}

// User profile from onboarding
export interface UserProfile {
  name: string;
  gender: "male" | "female" | "other" | "prefer-not-to-say" | "";
  speakingStyle: "beginner" | "local" | "other" | "";
  // When speakingStyle is "other", these are the selected custom styles
  customStyles: string[];
  // Known languages beyond English (ISO 639-1 codes)
  knownLanguages: KnownLanguageCode[];
}

// Theme types
export type Theme = "light" | "dark" | "system";

interface SettingsContextType {
  tts: TTSSettings;
  debugMode: boolean;
  theme: Theme;
  userProfile: UserProfile;
  hasCompletedOnboarding: boolean;
  hasSeenLearningWalkthrough: boolean;
  analyticsOptOut: boolean;
  showInlineVocalizations: boolean;
  userDetails: Record<string, string>;
  setTTSSettings: (settings: Partial<TTSSettings>) => void;
  setDebugMode: (enabled: boolean) => void;
  setTheme: (theme: Theme) => void;
  setUserProfile: (profile: UserProfile) => void;
  completeOnboarding: (profile: UserProfile) => void;
  completeLearningWalkthrough: () => void;
  resetOnboarding: () => void;
  setAnalyticsOptOut: (optOut: boolean) => void;
  setShowInlineVocalizations: (show: boolean) => void;
  setUserDetails: (details: Record<string, string>) => void;
}

const SettingsContext = createContext<SettingsContextType | null>(null);

const STORAGE_KEY = "converso-settings";

const DEFAULT_TTS_SETTINGS: TTSSettings = {
  speed: 0.9,
};

const DEFAULT_USER_PROFILE: UserProfile = {
  name: "",
  gender: "",
  speakingStyle: "",
  customStyles: [],
  knownLanguages: [],
};

const DEFAULT_USER_DETAILS: Record<string, string> = {};

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [tts, setTTS] = useState<TTSSettings>(DEFAULT_TTS_SETTINGS);
  const [debugMode, setDebugModeState] = useState(false);
  const [theme, setThemeState] = useState<Theme>("system");
  const [userProfile, setUserProfileState] = useState<UserProfile>(DEFAULT_USER_PROFILE);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [hasSeenLearningWalkthrough, setHasSeenLearningWalkthrough] = useState(false);
  const [analyticsOptOut, setAnalyticsOptOutState] = useState(false);
  const [showInlineVocalizations, setShowInlineVocalizationsState] = useState(true);
  const [userDetails, setUserDetailsState] = useState<Record<string, string>>(DEFAULT_USER_DETAILS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Migrate old TTS settings: only keep speed
        if (parsed.tts?.speed !== undefined) {
          setTTS({ speed: parsed.tts.speed });
        }

        // Load debug mode
        if (parsed.debugMode !== undefined) {
          setDebugModeState(parsed.debugMode);
        }

        // Load theme
        if (parsed.theme) {
          setThemeState(parsed.theme);
        }

        // Load user profile
        if (parsed.userProfile) {
          setUserProfileState({ ...DEFAULT_USER_PROFILE, ...parsed.userProfile });
        }

        // Load onboarding state
        if (parsed.hasCompletedOnboarding !== undefined) {
          setHasCompletedOnboarding(parsed.hasCompletedOnboarding);
        }

        // Load learning walkthrough state
        if (parsed.hasSeenLearningWalkthrough !== undefined) {
          setHasSeenLearningWalkthrough(parsed.hasSeenLearningWalkthrough);
        }

        // Load analytics opt-out state
        if (parsed.analyticsOptOut !== undefined) {
          setAnalyticsOptOutState(parsed.analyticsOptOut);
        }

        // Load inline vocalizations setting
        if (parsed.showInlineVocalizations !== undefined) {
          setShowInlineVocalizationsState(parsed.showInlineVocalizations);
        }

        // Load user details
        if (parsed.userDetails) {
          setUserDetailsState(parsed.userDetails);
        }
      }
    } catch {
      // Ignore storage errors
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          tts,
          debugMode,
          theme,
          userProfile,
          hasCompletedOnboarding,
          hasSeenLearningWalkthrough,
          analyticsOptOut,
          showInlineVocalizations,
          userDetails,
        }));
      } catch {
        // Ignore storage errors
      }
    }
  }, [tts, debugMode, theme, userProfile, hasCompletedOnboarding, hasSeenLearningWalkthrough, analyticsOptOut, showInlineVocalizations, userDetails, isLoaded]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    };

    if (theme === "system") {
      // Listen to system preference
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      applyTheme(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    } else {
      applyTheme(theme === "dark");
      return undefined;
    }
  }, [theme]);

  const setDebugMode = (enabled: boolean) => {
    setDebugModeState(enabled);
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const setUserProfile = (profile: UserProfile) => {
    setUserProfileState(profile);
  };

  const completeOnboarding = (profile: UserProfile) => {
    setUserProfileState(profile);
    setHasCompletedOnboarding(true);
  };

  const completeLearningWalkthrough = () => {
    setHasSeenLearningWalkthrough(true);
  };

  const resetOnboarding = () => {
    setHasCompletedOnboarding(false);
    setHasSeenLearningWalkthrough(false);
    setUserProfileState(DEFAULT_USER_PROFILE);
  };

  const setAnalyticsOptOut = (optOut: boolean) => {
    setAnalyticsOptOutState(optOut);
  };

  const setShowInlineVocalizations = (show: boolean) => {
    setShowInlineVocalizationsState(show);
  };

  const setUserDetails = (details: Record<string, string>) => {
    setUserDetailsState(details);
  };

  const setTTSSettings = (settings: Partial<TTSSettings>) => {
    setTTS((prev) => ({ ...prev, ...settings }));
  };

  return (
    <SettingsContext.Provider
      value={{
        tts,
        debugMode,
        theme,
        userProfile,
        hasCompletedOnboarding,
        hasSeenLearningWalkthrough,
        analyticsOptOut,
        showInlineVocalizations,
        userDetails,
        setTTSSettings,
        setDebugMode,
        setTheme,
        setUserProfile,
        completeOnboarding,
        completeLearningWalkthrough,
        resetOnboarding,
        setAnalyticsOptOut,
        setShowInlineVocalizations,
        setUserDetails,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
